import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

// Inline images are allowed ONLY when they point at our own auth-gated
// attachment endpoint (`/api/attachments/<id>/raw`). A third-party
// `![](https://evil/track.png)` would exfiltrate the IP, User-Agent, and load
// timing of every reader — so any <img> whose src isn't same-origin-ours is
// stripped by the afterSanitizeElements hook below. The RichTextEditor only
// ever emits this same-origin form (it uploads pastes/drops to R2 first).
export const ATTACHMENT_IMG_SRC_RE =
  /^\/api\/attachments\/[A-Za-z0-9_-]+\/raw$/;

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p",
    "a",
    "code",
    "pre",
    "ul",
    "ol",
    "li",
    "strong",
    "em",
    "blockquote",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "br",
    "hr",
    "img",
  ],
  ALLOWED_ATTR: ["href", "title", "rel", "target", "src", "alt"],
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

// Strip any image that isn't one of our own attachment URLs (privacy: no
// third-party image beacons). DOMPurify has already normalised src by the
// time this element hook runs.
DOMPurify.addHook("afterSanitizeElements", (node) => {
  if (
    node.nodeName === "IMG" &&
    !ATTACHMENT_IMG_SRC_RE.test((node as Element).getAttribute?.("src") || "")
  ) {
    node.parentNode?.removeChild(node);
  }
});

// Force every anchor to open in a new tab with rel="noopener noreferrer".
// Without noopener the destination page gets `window.opener` access to the
// SPA origin (tabnabbing); without noreferrer it sees our URL in the Referer.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    const href = node.getAttribute("href") || "";
    // @mention links point at an in-app profile (/team/<id>). Style them as a
    // chip and keep them same-tab. The class is set here, after attribute
    // sanitization, so it persists without widening ALLOWED_ATTR; `mention` is
    // a constant we control — no injection risk.
    if (href.startsWith("/team/")) {
      node.setAttribute("class", "mention");
      node.setAttribute("target", "_self");
      node.removeAttribute("rel");
    } else if (href.startsWith("/")) {
      // Internal app link (e.g. a [[slug]] wiki link) — navigate same-tab.
      node.setAttribute("target", "_self");
      node.removeAttribute("rel");
    } else {
      node.setAttribute("rel", "noopener noreferrer");
      node.setAttribute("target", "_blank");
    }
  }
});

export function renderMarkdown(source: string): string {
  const html = md.render(source);
  return DOMPurify.sanitize(html, PURIFY_CONFIG);
}
